import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './CourseAutocomplete.css';

const MIN_QUERY_LEN = 2;

/**
 * Debounced (300ms) course-name autocomplete backed by GET /api/courses/typeahead.
 * Drop-in replacement for a plain text <input> — `value`/`onChange` behave the
 * same way, plus `onSelect(course)` fires when the user picks a suggestion
 * (or completes an inline "add it") so parent forms can autofill city/state.
 *
 * Two "can't find your course? Add it" modes, picked via `inlineAdd`:
 *  - inlineAdd=false (forms that already have their own City/State inputs):
 *    clicking "Add it" just closes the dropdown and leaves the typed text —
 *    the parent is expected to call api.addCourse(...) itself on submit.
 *  - inlineAdd=true (single-field contexts): clicking "Add it" expands small
 *    City/State inputs inline and calls api.addCourse(...) directly.
 */
function CourseAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Search for a course…',
  inlineAdd = false,
  required = false,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addCity, setAddCity] = useState('');
  const [addState, setAddState] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState('');

  const wrapperRef = useRef(null);
  // Holds the exact string WE just set `value` to (via pick / inline-add), so the
  // effect below can recognize "this change came from us" and skip re-querying.
  // Comparing against the specific value (not a plain boolean) matters: if the
  // picked name happens to equal the value already in the box, the effect never
  // re-runs to consume a boolean flag, which would otherwise wrongly suppress
  // the *next* real search. It's cleared on every manual keystroke too, so a
  // stale marker can never linger past the user's next edit. Seeded with the
  // initial value so a prefilled form (saved home course, "Log this round")
  // doesn't pop the dropdown open on load before anyone has typed.
  const skipFetchForValueRef = useRef(value || null);
  const seqRef = useRef(0); // guards against a slow, stale request clobbering a newer one

  // Debounced search — same 300ms-debounce-in-useEffect pattern as Search.js.
  useEffect(() => {
    if (skipFetchForValueRef.current !== null && skipFetchForValueRef.current === value) {
      skipFetchForValueRef.current = null;
      return;
    }
    const q = (value || '').trim();
    if (q.length < MIN_QUERY_LEN) {
      setOpen(false);
      setResults([]);
      setLoading(false);
      return;
    }
    setOpen(true);
    setLoading(true);
    const mySeq = ++seqRef.current;
    const t = setTimeout(async () => {
      try {
        const rows = await api.typeaheadCourses(q);
        if (seqRef.current === mySeq) {
          setResults(rows);
          setHighlighted(-1);
        }
      } catch {
        if (seqRef.current === mySeq) setResults([]);
      } finally {
        if (seqRef.current === mySeq) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value]);

  // Click-outside closes the dropdown
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setShowAddForm(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleInputChange = (e) => {
    skipFetchForValueRef.current = null; // any manual typing invalidates a pending skip
    onChange(e.target.value);
  };

  const handleFocus = () => {
    if ((value || '').trim().length >= MIN_QUERY_LEN) setOpen(true);
  };

  const handlePick = (course) => {
    skipFetchForValueRef.current = course.name;
    setOpen(false);
    setShowAddForm(false);
    onChange(course.name);
    onSelect && onSelect(course);
    api.selectCourse(course.id).catch(() => {}); // fire-and-forget usage tracker
  };

  const handleAddItClick = () => {
    setAddError('');
    if (inlineAdd) {
      setShowAddForm(true);
    } else {
      setOpen(false);
      setShowAddForm(false);
    }
  };

  const handleInlineAddConfirm = async () => {
    const name = (value || '').trim();
    if (!name) return;
    setAddSubmitting(true);
    setAddError('');
    try {
      const course = await api.addCourse({
        name,
        city: addCity.trim() || undefined,
        state: addState.trim() || undefined,
      });
      skipFetchForValueRef.current = course.name;
      onChange(course.name);
      onSelect && onSelect(course);
      api.selectCourse(course.id).catch(() => {});
      setOpen(false);
      setShowAddForm(false);
      setAddCity('');
      setAddState('');
    } catch (err) {
      setAddError(err.message || 'Failed to add course');
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    const itemCount = results.length + 1; // + the "Add it" footer row

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, itemCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlighted === -1) return; // let a normal form submit happen
      e.preventDefault();
      if (highlighted < results.length) {
        handlePick(results[highlighted]);
      } else {
        handleAddItClick();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setShowAddForm(false);
    }
  };

  return (
    <div className={`course-ac ${className}`} ref={wrapperRef}>
      <input
        type="text"
        className="form-input course-ac-input"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        required={required}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />

      {open && (
        <div className="course-ac-dropdown">
          {loading && results.length === 0 ? (
            <div className="course-ac-status">Searching…</div>
          ) : results.length === 0 ? (
            <div className="course-ac-status">No matching courses</div>
          ) : (
            <ul className="course-ac-list" role="listbox">
              {results.map((c, i) => (
                <li
                  key={c.id}
                  role="option"
                  aria-selected={highlighted === i}
                  className={`course-ac-item ${highlighted === i ? 'is-highlighted' : ''}`}
                  onClick={() => handlePick(c)}
                  onMouseEnter={() => setHighlighted(i)}
                >
                  <span className="course-ac-item-name">{c.name}</span>
                  {(c.city || c.state) && (
                    <span className="course-ac-item-sub">{[c.city, c.state].filter(Boolean).join(', ')}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="course-ac-footer">
            {inlineAdd && showAddForm ? (
              <div className="course-ac-inline-add">
                <p className="course-ac-inline-add-label">Add "{(value || '').trim()}" as a new course</p>
                <div className="course-ac-inline-add-row">
                  <input
                    type="text"
                    className="form-input course-ac-inline-input"
                    placeholder="City"
                    value={addCity}
                    onChange={(e) => setAddCity(e.target.value)}
                  />
                  <input
                    type="text"
                    className="form-input course-ac-inline-input course-ac-inline-state"
                    placeholder="ST"
                    maxLength={2}
                    value={addState}
                    onChange={(e) => setAddState(e.target.value)}
                  />
                </div>
                {addError && <p className="course-ac-inline-error">{addError}</p>}
                <div className="course-ac-inline-add-actions">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => { setShowAddForm(false); setAddError(''); }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleInlineAddConfirm}
                    disabled={addSubmitting || !(value || '').trim()}
                  >
                    {addSubmitting ? 'Adding…' : 'Confirm'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className={`course-ac-add-btn ${highlighted === results.length ? 'is-highlighted' : ''}`}
                onClick={handleAddItClick}
                onMouseEnter={() => setHighlighted(results.length)}
              >
                Can't find your course? <strong>Add it</strong>
              </button>
            )}

            <p className="course-ac-attribution">
              Course data ©{' '}
              <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
                OpenStreetMap contributors
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseAutocomplete;
